/**
 * Web API implementation
 * Calls backend REST APIs, mounted to window.api by web-entry.tsx via createWebAPI()
 */

import { decryptData, encryptData, type DecryptResult, type ExportData } from '../utils/encryption';
import type { ChapterStatus, PlotThreadKind, PlotThreadStatus } from '@xiaotianshu/shared';
import { logger } from '../utils/logger';

function clearSessionCookie() {
  document.cookie = 'sw_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  sessionStorage.removeItem('sw_authenticated');
}

function handleSessionExpired() {
  clearSessionCookie();
  window.location.hash = '#/';
}

/**
 * 触发浏览器下载，并**如实回报是否可能失败**。
 *
 * 为什么需要判断：某些宿主环境会**主动阻止下载** ——
 * DSH 内嵌浏览器就监听 `will-download` 并调用 `event.preventDefault()`
 * （同时把所有权限请求设为 deny）。此时 `a.click()` 不报错、也没有任何提示，
 * 文件根本不会落盘。若仍回报成功，用户会以为导出成功而去文件夹里找。
 *
 * 浏览器没有"下载是否被取消"的同步 API，这里用两个启发式信号：
 *   1. 宿主主动声明了下载不可用（`window.__DISABLE_DOWNLOAD__`，供宿主注入）
 *   2. 点击过程抛错
 * 因此返回值语义是"**未能确认下载**"，调用方据此给出可操作的提示。
 */
export function downloadTextFile(
  content: string,
  filename: string,
  mime = 'application/octet-stream',
): { success: boolean; error?: string } {
  try {
    if ((window as unknown as { __DISABLE_DOWNLOAD__?: boolean }).__DISABLE_DOWNLOAD__ === true) {
      return { success: false, error: 'DOWNLOAD_BLOCKED' };
    }
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // 延迟释放：某些宿主在 click 之后才异步读取 blob，太早 revoke 会拿到空文件
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

function fetchAPI(path: string, options: RequestInit = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...((options.headers as Record<string, string>) || {}) };
  return fetch(path, { ...options, headers, credentials: 'include' })
    .then(async r => {
      logger.setRequestId(r.headers.get('X-Request-Id') || undefined);
      if (r.status === 401) {
        logger.info('api unauthorized', { path, method, status: 401 });
        handleSessionExpired();
        throw new Error('Session expired, please re-authenticate');
      }
      if (!r.ok) {
        const e = await r.json().catch(() => ({ error: r.statusText }));
        logger.warn('api request failed', { path, method, status: r.status, error: e.error });
        throw new Error(e.error || r.statusText);
      }
      return r.json();
    })
    .catch((err) => {
      if (err instanceof TypeError) {
        logger.error('api network error', { path, method, error: err.message });
      }
      throw err;
    });
}

export function createWebAPI() {
  const api = {
    getAppVersion: () => Promise.resolve(__APP_VERSION__),

    registerUser: async () => ({ success: false, error: 'Registration not needed' }),
    loginUser: async () => ({ success: false, error: 'Please use password verification' }),

    verifyPassword: async (password: string) => {
      try {
        const r = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ password }),
        });
        if (r.ok) return { success: true };
        if (r.status === 401) {
          const d = await r.json().catch(() => ({ error: 'Invalid password' }));
          return { success: false, error: d.error || 'Invalid password' };
        }
        const d = await r.json().catch(() => ({ error: 'Verification failed' }));
        return { success: false, error: d.error || 'Verification failed' };
      } catch (err: any) {
        return { success: false, error: 'Invalid password' };
      }
    },
    changePassword: async (password: string) => {
      try {
        await fetchAPI('/api/auth/password', { method: 'PUT', body: JSON.stringify({ password }) });
        return { success: true };
      } catch (err: any) {
        return { success: false, error: err.message || 'Change failed' };
      }
    },
    getCurrentUser: async (): Promise<null> => null,
    logoutUser: async () => {
      // 优先走服务端登出（可靠清除 httpOnly Cookie）；失败时本地清理兜底
      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      } catch { /* 网络异常时忽略，本地清理即可 */ }
      clearSessionCookie();
    },

    getBooks: (options?: { sort?: 'created' | 'recent' }) => {
      const qs = options?.sort === 'recent' ? '?sort=recent' : '';
      return fetchAPI(`/api/books${qs}`);
    },
    createBook: (data: { title: string; description: string; cover: string }) =>
      fetchAPI('/api/books', { method: 'POST', body: JSON.stringify(data) }),
    updateBook: (data: { id: string; title: string; description: string; cover: string }) =>
      fetchAPI(`/api/books/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteBooks: (ids: string[]) =>
      fetchAPI('/api/books/delete', { method: 'PATCH', body: JSON.stringify({ ids }) }),
    getDeletedBooks: () => fetchAPI('/api/books/deleted'),
    restoreBooks: (ids: string[]) =>
      fetchAPI('/api/books/restore', { method: 'PATCH', body: JSON.stringify({ ids }) }),
    permanentlyDeleteBooks: (ids: string[]) =>
      fetchAPI('/api/books', { method: 'DELETE', body: JSON.stringify({ ids }) }),

    getVolumes: (bookId: string) => fetchAPI(`/api/books/${bookId}/volumes`),
    createVolume: (data: { bookId: string; title: string; description: string }) =>
      fetchAPI(`/api/books/${data.bookId}/volumes`, { method: 'POST', body: JSON.stringify(data) }),
    deleteVolume: (id: string, options?: { moveToVolumeId?: string }) =>
      fetchAPI(`/api/volumes/${id}`, {
        method: 'DELETE',
        body: JSON.stringify(options?.moveToVolumeId ? { moveToVolumeId: options.moveToVolumeId } : {}),
      }),
    updateVolume: (data: { id: string; title: string; description: string }) =>
      fetchAPI(`/api/volumes/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    commitBookStructure: (data: {
      bookId: string;
      volumes: { id: string; title: string; description: string }[];
      chapters: { id: string; volumeId: string; title: string; summary: string }[];
    }) => {
      const { bookId, ...body } = data;
      return fetchAPI(`/api/books/${bookId}/structure`, { method: 'POST', body: JSON.stringify(body) });
    },

    getChapters: (volumeId: string) => fetchAPI(`/api/volumes/${volumeId}/chapters`),
    getAllChaptersByBook: (bookId: string) => fetchAPI(`/api/books/${bookId}/chapters`),
    getChapterContent: (chapterId: string) =>
      fetchAPI(`/api/chapters/${chapterId}/content`).then(d => d.content),
    getChapterMetadata: (chapterId: string) => fetchAPI(`/api/chapters/${chapterId}`),
    createChapter: (data: { volumeId: string; title: string; summary: string; content: string; afterChapterNumber?: number }) =>
      fetchAPI(`/api/volumes/${data.volumeId}/chapters`, { method: 'POST', body: JSON.stringify(data) }),
    deleteChapter: (id: string) =>
      fetchAPI(`/api/chapters/${id}`, { method: 'DELETE' }),
    updateChapter: (data: { id: string; title?: string; summary?: string; content?: string; status?: ChapterStatus; targetWords?: number }) =>
      fetchAPI(`/api/chapters/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    moveChapter: (data: { chapterId: string; targetChapterNumber: number }) =>
      fetchAPI('/api/chapters/move', { method: 'PATCH', body: JSON.stringify(data) }),
    moveChapterToVolume: (data: { chapterId: string; volumeId: string }) =>
      fetchAPI(`/api/chapters/${data.chapterId}/volume`, { method: 'PATCH', body: JSON.stringify({ volumeId: data.volumeId }) }),
    searchChapters: (bookId: string, params?: { query?: string; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.query) qs.set('q', params.query);
      if (params?.limit) qs.set('limit', String(params.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return fetchAPI(`/api/books/${bookId}/search${suffix}`).then(d => d.hits ?? []);
    },

    getPlotThreads: (bookId: string, options?: { status?: PlotThreadStatus }) => {
      const suffix = options?.status ? `?status=${encodeURIComponent(options.status)}` : '';
      return fetchAPI(`/api/books/${bookId}/threads${suffix}`);
    },
    createPlotThread: (data: {
      bookId: string; kind?: PlotThreadKind; title: string; detail?: string; status?: PlotThreadStatus;
      plantedChapterId?: string | null; resolvedChapterId?: string | null;
      quote?: string; quoteStart?: number | null; quoteEnd?: number | null;
    }) => {
      const { bookId, ...body } = data;
      return fetchAPI(`/api/books/${bookId}/threads`, { method: 'POST', body: JSON.stringify(body) });
    },
    updatePlotThread: (data: {
      id: string; kind?: PlotThreadKind; title?: string; detail?: string; status?: PlotThreadStatus;
      plantedChapterId?: string | null; resolvedChapterId?: string | null;
      quote?: string; quoteStart?: number | null; quoteEnd?: number | null;
    }) => {
      const { id, ...body } = data;
      return fetchAPI(`/api/threads/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    },
    deletePlotThread: (id: string) =>
      fetchAPI(`/api/threads/${id}`, { method: 'DELETE' }),

    getCharacterRelations: (bookId: string) =>
      fetchAPI(`/api/books/${bookId}/relations`),
    createCharacterRelation: (data: {
      bookId: string; fromCharacterId: string; toCharacterId: string;
      relationType: string; bidirectional?: boolean; note?: string;
    }) => {
      const { bookId, ...body } = data;
      return fetchAPI(`/api/books/${bookId}/relations`, { method: 'POST', body: JSON.stringify(body) });
    },
    updateCharacterRelation: (data: {
      id: string; relationType?: string; bidirectional?: boolean; note?: string;
    }) => {
      const { id, ...body } = data;
      return fetchAPI(`/api/relations/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    },
    deleteCharacterRelation: (id: string) =>
      fetchAPI(`/api/relations/${id}`, { method: 'DELETE' }),
    getCharacterAppearances: (characterId: string, options?: { limit?: number }) => {
      const suffix = options?.limit ? `?limit=${options.limit}` : '';
      return fetchAPI(`/api/characters/${characterId}/appearances${suffix}`).then(d => d.hits ?? []);
    },

    createChapterVersion: (data: { chapterId: string; bookId: string; title: string; summary: string; content: string; wordCount: number; source: string }) =>
      fetchAPI(`/api/chapters/${data.chapterId}/versions`, {
        method: 'POST',
        body: JSON.stringify({ bookId: data.bookId, title: data.title, summary: data.summary, content: data.content, wordCount: data.wordCount, source: data.source }),
      }).then(d => d.id as string),
    getChapterVersions: (chapterId: string) => fetchAPI(`/api/chapters/${chapterId}/versions`),
    getChapterVersionCount: (chapterId: string, filter?: { source?: string; notSource?: string }) => {
      const params = new URLSearchParams();
      if (filter?.source) params.set('source', filter.source);
      if (filter?.notSource) params.set('notSource', filter.notSource);
      const qs = params.toString();
      return fetchAPI(`/api/chapters/${chapterId}/versions/count${qs ? `?${qs}` : ''}`).then(d => d.count as number);
    },
    deleteOldestVersions: (chapterId: string, keepCount: number, filter?: { source?: string; notSource?: string }) =>
      fetchAPI('/api/chapter-versions/oldest', {
        method: 'DELETE',
        body: JSON.stringify({ chapterId, keepCount, source: filter?.source, notSource: filter?.notSource }),
      }),
    updateChapterVersion: (data: { id: string; title: string; summary: string; content: string; wordCount: number }) =>
      fetchAPI(`/api/chapter-versions/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({ title: data.title, summary: data.summary, content: data.content, wordCount: data.wordCount }),
      }),
    deleteChapterVersion: (id: string) =>
      fetchAPI(`/api/chapter-versions/${id}`, { method: 'DELETE' }),

    getOutline: (bookId: string) =>
      fetchAPI(`/api/books/${bookId}/outline`).then(d => d.content),
    updateOutline: (data: { bookId: string; content: string }) =>
      fetchAPI(`/api/books/${data.bookId}/outline`, { method: 'PUT', body: JSON.stringify({ content: data.content }) }),

    getSettings: (bookId: string) => fetchAPI(`/api/books/${bookId}/settings`),
    getWorldData: (bookId: string) => fetchAPI(`/api/books/${bookId}/world-data`),
    createSetting: (data: { bookId: string; title: string; content: string }) =>
      fetchAPI(`/api/books/${data.bookId}/settings`, { method: 'POST', body: JSON.stringify(data) }),
    updateSetting: (data: { id: string; title: string; content: string }) =>
      fetchAPI(`/api/settings/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteSetting: (id: string) =>
      fetchAPI(`/api/settings/${id}`, { method: 'DELETE' }),

    getFactions: (bookId: string) => fetchAPI(`/api/books/${bookId}/factions`),
    factionNameExists: async (bookId: string, name: string, excludeId?: string) => {
      const list = await fetchAPI(`/api/books/${bookId}/factions`);
      return list.some((f: any) => f.name === name && (excludeId === undefined || f.id !== excludeId));
    },
    createFaction: (data: { bookId: string; name: string; parentFactionId: string | null; description: string }) =>
      fetchAPI(`/api/books/${data.bookId}/factions`, { method: 'POST', body: JSON.stringify(data) }),
    updateFaction: (data: { id: string; name: string; parentFactionId: string | null; description: string }) =>
      fetchAPI(`/api/factions/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteFaction: (id: string) =>
      fetchAPI(`/api/factions/${id}`, { method: 'DELETE' }),
    getFactionTree: async (bookId: string, factionId: string) => {
      const all = await fetchAPI(`/api/books/${bookId}/factions`);
      const factionMap = new Map<string, any>();
      for (const f of all) factionMap.set(f.id, f);
      const start: any = factionMap.get(factionId);
      if (!start) return [];
      let root = start;
      while (root.parent_faction_id && factionMap.has(root.parent_faction_id)) {
        root = factionMap.get(root.parent_faction_id);
      }
      const tree: any[] = [];
      const queue = [root];
      while (queue.length > 0) {
        const current = queue.shift()!;
        tree.push(current);
        for (const f of all) {
          if (f.parent_faction_id === current.id) queue.push(f);
        }
      }
      return tree;
    },

    getCharacters: (bookId: string) => fetchAPI(`/api/books/${bookId}/characters`),
    characterNameExists: async (bookId: string, name: string, excludeId?: string) => {
      const list = await fetchAPI(`/api/books/${bookId}/characters`);
      return list.some((c: any) => c.name === name && (excludeId === undefined || c.id !== excludeId));
    },
    createCharacter: (data: { bookId: string; name: string; age: string | null; factionId: string | null; strength: string | null; description: string }) =>
      fetchAPI(`/api/books/${data.bookId}/characters`, { method: 'POST', body: JSON.stringify(data) }),
    updateCharacter: (data: { id: string; name: string; age: string | null; factionId: string | null; strength: string | null; description: string }) =>
      fetchAPI(`/api/characters/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteCharacter: (id: string) =>
      fetchAPI(`/api/characters/${id}`, { method: 'DELETE' }),

    getItems: (bookId: string) => fetchAPI(`/api/books/${bookId}/items`),
    itemNameExists: async (bookId: string, name: string, excludeId?: string) => {
      const list = await fetchAPI(`/api/books/${bookId}/items`);
      return list.some((i: any) => i.name === name && (excludeId === undefined || i.id !== excludeId));
    },
    createItem: (data: { bookId: string; name: string; type: string; description: string }) =>
      fetchAPI(`/api/books/${data.bookId}/items`, { method: 'POST', body: JSON.stringify(data) }),
    updateItem: (data: { id: string; name: string; type: string; description: string }) =>
      fetchAPI(`/api/items/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteItem: (id: string) =>
      fetchAPI(`/api/items/${id}`, { method: 'DELETE' }),

    getUserPreference: (preferenceName: string) =>
      fetchAPI(`/api/user/preferences/${preferenceName}`),
    setUserPreference: (preferenceName: string, preferenceValue: string | null) =>
      fetchAPI(`/api/user/preferences/${preferenceName}`, { method: 'PUT', body: JSON.stringify({ value: preferenceValue }) }),
    getAllUserPreferences: () =>
      fetchAPI('/api/user/preferences'),

    getAIPromptTemplates: () => fetchAPI('/api/ai/prompt-templates'),
    createAIPromptTemplate: (data: { name: string; content: string }) =>
      fetchAPI('/api/ai/prompt-templates', { method: 'POST', body: JSON.stringify(data) }),
    updateAIPromptTemplate: (data: { id: string; name: string; content: string }) =>
      fetchAPI(`/api/ai/prompt-templates/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteAIPromptTemplate: (id: string) =>
      fetchAPI(`/api/ai/prompt-templates/${id}`, { method: 'DELETE' }),

    getAIChatSessions: (query?: { bookId?: string; mode?: string; fields?: string }) => {
      const params = new URLSearchParams();
      if (query?.bookId) params.set('bookId', query.bookId);
      if (query?.mode) params.set('mode', query.mode);
      if (query?.fields) params.set('fields', query.fields);
      const qs = params.toString();
      return fetchAPI(`/api/ai/chat-sessions${qs ? `?${qs}` : ''}`);
    },
    getAIChatSessionMessages: (sessionUuid: string) =>
      fetchAPI(`/api/ai/chat-sessions/${sessionUuid}/messages`).then(d => d.messages),
    upsertAIChatSession: (data: { session_uuid: string; book_id?: string; mode?: string; model_name?: string; provider?: string; title?: string; messages?: string; activity_snapshot?: string; chapter_id?: string; chapter_title?: string }) =>
      fetchAPI('/api/ai/chat-sessions', { method: 'POST', body: JSON.stringify(data) }),
    deleteAIChatSession: (sessionUuid: string) =>
      fetchAPI(`/api/ai/chat-sessions/${sessionUuid}`, { method: 'DELETE' }),
    batchDeleteAIChatSessions: (uuids: string[]) =>
      fetchAPI('/api/ai/chat-sessions/batch-delete', { method: 'POST', body: JSON.stringify({ uuids }) }),

    exportBook: async (bookIds: string[]) => {
      const r = await fetchAPI('/api/books/export', { method: 'POST', body: JSON.stringify({ ids: bookIds }) });
      return r;
    },
    encryptExportData: async (bookDataList: unknown[], password: string) => {
      try {
        const batchExportData = {
          version: '1.0',
          type: 'xiaotianshu_batch_export',
          timestamp: new Date().toISOString(),
          books: bookDataList,
        };
        const encrypted = await encryptData(batchExportData as unknown as ExportData, password);
        return { success: true, data: encrypted };
      } catch (error) {
        return { success: false, error: String(error instanceof Error ? error.message : error) };
      }
    },
    saveExportFile: (encryptedData: string) => {
      // tryDownload 会返回 false 的情况：宿主环境阻止了下载
      // （例如 DSH 内嵌浏览器在 will-download 里 event.preventDefault()），
      // 此时**不能**回报成功，否则用户以为导出成功却找不到文件。
      return Promise.resolve(downloadTextFile(
        encryptedData,
        `xiaotianshu-backup-${new Date().toISOString().slice(0, 10)}.xts`,
        'application/octet-stream',
      ));
    },
    importBookFile: () => new Promise<{
      success: boolean; data?: string; binary?: ArrayBuffer;
      fileName?: string; kind?: 'backup' | 'word' | 'unknown';
      error?: string; canceled?: boolean;
    }>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      // 只接受两类：
      //   .xts              小天书加密备份（可完整还原）
      //   .docx/.doc        Word 稿件（从其他写作软件迁移，只取章节与正文）
      // TXT/Markdown 是**导出成品**，不含结构信息，无法还原，故不再列为可导入格式。
      input.accept = '.xts,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) { resolve({ success: false, canceled: true }); return; }
        const name = file.name.toLowerCase();
        // 按扩展名决定读取方式：Word 是二进制 zip，必须按 ArrayBuffer 读
        const isWord = name.endsWith('.docx') || name.endsWith('.doc');
        const isBackup = name.endsWith('.xts');
        const kind = isBackup ? 'backup' : isWord ? 'word' : 'unknown';
        const reader = new FileReader();
        reader.onload = () => {
          if (isWord) {
            // .doc（旧二进制格式）不是 zip，解析会失败并给出明确提示
            resolve({ success: true, binary: reader.result as ArrayBuffer, fileName: file.name, kind: 'word' });
          } else {
            resolve({ success: true, data: reader.result as string, fileName: file.name, kind });
          }
        };
        reader.onerror = () => resolve({ success: false, canceled: false, error: '文件读取失败' });
        if (isWord) reader.readAsArrayBuffer(file);
        else reader.readAsText(file);
      };
      input.click();
    }),
    decryptImportData: async (encryptedData: string, password: string) => {
      const result: DecryptResult = await decryptData(encryptedData, password);
      if (result.ok === false) {
        return { success: false as const, error: result.message, reason: result.reason };
      }
      return { success: true as const, data: result.data };
    },
    importBookData: async (importedData: unknown) => {
      const r = await fetchAPI('/api/books/import', { method: 'POST', body: JSON.stringify(importedData) });
      return r;
    },

    copyToClipboard: (text: string) => { navigator.clipboard.writeText(text); return Promise.resolve(true); },
  };

  return api;
}
